import type {
  CustomerOffer,
  CustomerOfferBindingDefinitions,
  CustomerOfferPreparedDocument,
} from "./types/customerOffers.types";
import {ZodError} from "zod";
import type {z} from "zod";
import type {CustomerOfferDto} from "@project/sdk/sales/zod";
import {
  CatalogItemFacts,
  ChapterPricing,
  CustomerOfferChapterReview,
  CustomerOfferPermittedAction,
  CustomerOfferWorkflow,
  CustomerSelection,
  OfferBindingValue,
  OfferCatalogBlock,
  OfferChapter,
  OfferContent,
  OfferLiteralValue,
  OfferRichTextBlock,
  OfferRichTextContent,
  OfferSupplierSelection,
  OfferVariable,
  PrepareCustomerOfferSendResponse,
  RepresentativeSelection,
} from "@project/sdk/sales/zod";

type Location = (string | number)[];
function invalid(path: Location, message: string): never {
  throw new ZodError([{code: "custom", path, message}]);
}
// Only called for the Sales objects explicitly declared additionalProperties:false.
// Currency, Money and Notes are open shared contracts and are not closed here.
function closed<DocumentFields extends Record<string, unknown>>(
  raw: DocumentFields | null | undefined,
  parsed: DocumentFields,
  path: Location,
  fields: readonly string[] = Object.keys(parsed),
): void {
  if (raw !== null && typeof raw === "object") {
    for (const key of Object.keys(raw)) {
      if (!fields.includes(key)) invalid([...path, key], "Undeclared document field");
    }
  }
}
export function assertCustomerOfferBindingDefinitions(
  definitions: CustomerOfferBindingDefinitions,
  raw: CustomerOfferBindingDefinitions,
): void {
  definitions.forEach((definition, index) => closed(raw[index], definition, [index]));
  unique(
    definitions.map((definition) => definition.key),
    [],
    "key",
  );
}
function availability<DocumentFields extends Record<string, unknown>>(
  object: DocumentFields,
  state: string,
  key: string,
  path: Location,
): void {
  const present = Object.hasOwn(object, key);
  if (state === "AVAILABLE" ? !present || object[key] === undefined : present) {
    invalid([...path, key], "Financial value must match its declared availability");
  }
}
function unique(values: string[], path: Location, key: string): void {
  const seen = new Set<string>();
  values.forEach((value, index) => {
    if (seen.has(value)) invalid([...path, index, key], "Duplicate identity in owning scope");
    seen.add(value);
  });
}

// Supplement authored conditional/closed-object rules after the exact OWN
// operation parse; no source acquisition, finance calculation or DTO rewrite.
export function assertCustomerOfferDocument(
  offer: CustomerOffer,
  raw: z.input<typeof CustomerOfferDto> = offer,
): void {
  closed(raw, offer, [], [
    "customerOfferId",
    "expedientId",
    "offerOrdinal",
    "version",
    "previousRevisionId",
    "name",
    "status",
    "documentSource",
    "language",
    "currency",
    "createdByUserId",
    "reviserUserId",
    "customer",
    "representative",
    "content",
    "evaluation",
    "notes",
    "workflow",
  ] satisfies readonly (keyof CustomerOffer)[]);
  closed(raw.customer, offer.customer, ["customer"], CustomerSelection.keyof().options);
  if (offer.representative)
    closed(
      raw.representative,
      offer.representative,
      ["representative"],
      RepresentativeSelection.keyof().options,
    );
  closed(raw.workflow, offer.workflow, ["workflow"], CustomerOfferWorkflow.keyof().options);
  offer.workflow.chapters.forEach((chapter, index) =>
    closed(
      raw.workflow.chapters[index],
      chapter,
      ["workflow", "chapters", index],
      CustomerOfferChapterReview.keyof().options,
    ),
  );
  offer.workflow.permittedActions.forEach((action, index) =>
    closed(
      raw.workflow.permittedActions[index],
      action,
      ["workflow", "permittedActions", index],
      CustomerOfferPermittedAction.keyof().options,
    ),
  );
  unique(
    offer.workflow.chapters.map((chapter) => chapter.chapterId.toLowerCase()),
    ["workflow", "chapters"],
    "chapterId",
  );
  const expectedChapters = offer.content.chapters
    .map((chapter) => chapter.chapterId.toLowerCase())
    .sort();
  if (
    JSON.stringify(expectedChapters) !==
    JSON.stringify(offer.workflow.chapters.map((chapter) => chapter.chapterId.toLowerCase()).sort())
  )
    invalid(["workflow", "chapters"], "Review progress must cover the actual authored Chapters");
  if (!offer.workflow.latestRevision && offer.workflow.permittedActions.length)
    invalid(
      ["workflow", "permittedActions"],
      "Historical revisions cannot advertise mutable actions",
    );
  const live = ["DRAFT", "PENDING", "COEFFICIENT_REVIEW", "APPROVED"].includes(offer.status);
  const issuedStatus = ["SENT", "ACCEPTED", "REJECTED", "CANCELED"].includes(offer.status);
  if (
    (live && offer.documentSource !== "LIVE") ||
    (issuedStatus && offer.documentSource !== "ISSUED") ||
    (offer.status === "OBSOLETE" &&
      (!offer.workflow.obsoleteFromStatus ||
        offer.workflow.obsoleteFromStatus === "OBSOLETE" ||
        (["SENT", "ACCEPTED", "REJECTED", "CANCELED"].includes(offer.workflow.obsoleteFromStatus)
          ? offer.documentSource !== "ISSUED"
          : offer.documentSource !== "LIVE")))
  ) {
    invalid(["documentSource"], "Document source does not match revision lifecycle");
  }
  if (offer.evaluation === undefined)
    invalid(["evaluation"], "Live and issued documents require evaluation");
  if (offer.status !== "OBSOLETE" && offer.workflow.obsoleteFromStatus !== null)
    invalid(["workflow", "obsoleteFromStatus"], "Only Obsolete has a recorded origin");
  const issued = offer.documentSource === "ISSUED";
  const contentRaw = raw.content;
  closed(contentRaw, offer.content, ["content"], OfferContent.keyof().options);
  const richText = (
    text: CustomerOffer["content"]["header"],
    original: CustomerOffer["content"]["header"] | undefined,
    path: Location,
  ) => {
    closed(original, text, path, OfferRichTextContent.keyof().options);
    unique(
      text.variables.map((item) => item.name),
      [...path, "variables"],
      "name",
    );
    text.variables.forEach((item, index) => {
      const variableRaw = original?.variables[index];
      const location = [...path, "variables", index];
      closed(variableRaw, item, location, OfferVariable.keyof().options);
      if (item.value !== null)
        closed(
          variableRaw?.value,
          item.value,
          [...location, "value"],
          item.value.type === "LITERAL"
            ? OfferLiteralValue.keyof().options
            : OfferBindingValue.keyof().options,
        );
      else if (issued) invalid([...location, "value"], "Issued variables must be resolved");
    });
  };
  richText(offer.content.header, contentRaw.header, ["content", "header"]);
  richText(offer.content.footer, contentRaw.footer, ["content", "footer"]);
  unique(
    offer.content.chapters.map((item) => item.chapterId.toLowerCase()),
    ["content", "chapters"],
    "chapterId",
  );
  offer.content.chapters.forEach((chapter, index) => {
    const original = contentRaw.chapters[index];
    const location = ["content", "chapters", index];
    closed(original, chapter, location, OfferChapter.keyof().options);
    if (typeof original?.applyCommission !== "boolean")
      invalid([...location, "applyCommission"], "Explicit commission applicability is required");
    unique(
      chapter.blocks.map((block) => block.blockId.toLowerCase()),
      [...location, "blocks"],
      "blockId",
    );
    chapter.blocks.forEach((block, blockIndex) => {
      const blockRaw = original?.blocks[blockIndex];
      const path = [...location, "blocks", blockIndex];
      closed(
        blockRaw,
        block,
        path,
        block.blockType === "RICH_TEXT"
          ? OfferRichTextBlock.keyof().options
          : OfferCatalogBlock.keyof().options,
      );
      if (block.blockType === "RICH_TEXT")
        richText(
          block.content,
          blockRaw?.blockType === "RICH_TEXT" ? blockRaw.content : undefined,
          [...path, "content"],
        );
      else {
        if (issued && block.quantity !== null && block.quantity <= 0)
          invalid([...path, "quantity"], "An explicit issued quantity must be positive");
        block.selectedSupplierOffers.forEach((selection, selectionIndex) =>
          closed(
            blockRaw?.blockType === "CATALOG_ITEM"
              ? blockRaw.selectedSupplierOffers[selectionIndex]
              : undefined,
            selection,
            [...path, "selectedSupplierOffers", selectionIndex],
            OfferSupplierSelection.keyof().options,
          ),
        );
      }
    });
  });
  const evaluation = offer.evaluation;
  if (!evaluation) return;
  const evaluationRaw = raw.evaluation;
  closed(evaluationRaw, evaluation, ["evaluation"], [
    "agreedCommissionAvailability",
    "agreedCommissionFraction",
    "exchangeRates",
    "catalogBlocks",
    "chapters",
    "variables",
    "financials",
    "issues",
  ] satisfies readonly (keyof NonNullable<CustomerOffer["evaluation"]>)[]);

  const rates = evaluation.exchangeRates;
  closed(
    evaluationRaw?.exchangeRates,
    rates,
    ["evaluation", "exchangeRates"],
    rates.status === "NOT_USED"
      ? ["status"]
      : rates.status === "AVAILABLE"
        ? ["status", "provider", "publicationDate"]
        : rates.reason === "MISSING_PUBLICATION"
          ? ["status", "provider", "reason", "requiredPublicationDate"]
          : ["status", "provider", "reason", "publicationDate", "requiredPublicationDate"],
  );
  if (issued && rates.status === "UNAVAILABLE")
    invalid(["evaluation", "exchangeRates"], "Issued conversion evidence cannot be unavailable");
  availability(evaluation, evaluation.agreedCommissionAvailability, "agreedCommissionFraction", [
    "evaluation",
  ]);
  if (issued && evaluation.issues.length !== 0)
    invalid(["evaluation", "issues"], "Issued documents cannot contain repairable live issues");
  evaluation.variables.forEach((variable, index) => {
    const path = ["evaluation", "variables", index];
    closed(evaluationRaw?.variables[index], variable, path, [
      "scope",
      "chapterId",
      "blockId",
      "name",
      "value",
    ] satisfies readonly (keyof typeof variable)[]);
    if (variable.scope === "BLOCK" && (!variable.chapterId || !variable.blockId))
      invalid(path, "Block variable requires its actual Chapter and Block");
  });
  evaluation.issues.forEach((issue, index) => {
    const path = ["evaluation", "issues", index];
    closed(evaluationRaw?.issues[index], issue, path, [
      "code",
      "scope",
      "chapterId",
      "blockId",
      "leafCatalogItemId",
      "variableName",
    ] satisfies readonly (keyof typeof issue)[]);
    if (
      issue.code === "VARIABLE_UNRESOLVED" &&
      (!issue.scope || issue.variableName === null || issue.variableName === undefined)
    )
      invalid(path, "Unresolved variable requires scope and name");
    const located =
      issue.scope === "BLOCK" ||
      [
        "SUPPLIER_NOT_SELECTABLE",
        "TERMS_UNPRICED",
        "CURRENCY_MISMATCH",
        "LEAF_COVERAGE_INVALID",
        "QUANTITY_UNRESOLVED",
      ].includes(issue.code);
    if (located && (!issue.chapterId || !issue.blockId))
      invalid(path, "Issue requires its actual Chapter and Block");
    if (
      ["SUPPLIER_NOT_SELECTABLE", "TERMS_UNPRICED", "CURRENCY_MISMATCH"].includes(issue.code) &&
      !issue.leafCatalogItemId
    )
      invalid(path, "Source issue requires its actual leaf");
  });
  evaluation.catalogBlocks.forEach((block, index) => {
    const path = ["evaluation", "catalogBlocks", index];
    const original = evaluationRaw?.catalogBlocks[index];
    closed(original, block, path, [
      "chapterId",
      "blockId",
      "soldCatalogItem",
      "selectedSupplierOffers",
      "quantity",
      "priceProtected",
      "costAvailability",
      "unitSupplierCost",
      "supplierCost",
    ] satisfies readonly (keyof typeof block)[]);
    if (block.soldCatalogItem)
      closed(
        original?.soldCatalogItem,
        block.soldCatalogItem,
        [...path, "soldCatalogItem"],
        CatalogItemFacts.keyof().options,
      );
    availability(block, block.costAvailability, "unitSupplierCost", path);
    availability(block, block.costAvailability, "supplierCost", path);
    if (
      issued &&
      (block.soldCatalogItem === null ||
        block.quantity === null ||
        block.quantity <= 0 ||
        block.costAvailability === "UNAVAILABLE")
    )
      invalid(path, "Issued source facts must be complete");
    block.selectedSupplierOffers.forEach((selection, selectionIndex) => {
      const location = [...path, "selectedSupplierOffers", selectionIndex];
      closed(original?.selectedSupplierOffers[selectionIndex], selection, location, [
        "supplierId",
        "catalogItemId",
        "supplierName",
        "priceProtected",
        "priceAvailability",
        "conversionAvailability",
        "conversion",
        "unitPrice",
        "minimumOrderQuantity",
        "orderQuantityMultiple",
        "quantityPerSoldUnit",
        "effectiveQuantity",
      ] satisfies readonly (keyof typeof selection)[]);
      availability(selection, selection.priceAvailability, "unitPrice", location);

      availability(selection, selection.conversionAvailability, "conversion", location);
      if (
        (selection.priceAvailability === "RESTRICTED") !==
        (selection.conversionAvailability === "RESTRICTED")
      )
        invalid(location, "Restricted native price and conversion must agree");
      if (
        selection.priceAvailability === "UNAVAILABLE" &&
        selection.conversionAvailability !== "UNAVAILABLE"
      )
        invalid(location, "Unavailable native source cannot disclose a conversion");
      const conversion = selection.conversion;
      if (conversion) {
        closed(
          original?.selectedSupplierOffers[selectionIndex]?.conversion,
          conversion,
          [...location, "conversion"],
          ["sourceCurrency", "targetCurrency", "factor"],
        );
        if (
          selection.priceAvailability !== "AVAILABLE" ||
          conversion.sourceCurrency !== selection.unitPrice?.currency.code ||
          conversion.targetCurrency !== offer.currency.code ||
          conversion.sourceCurrency === conversion.targetCurrency ||
          rates.status !== "AVAILABLE"
        )
          invalid(
            [...location, "conversion"],
            "Conversion must match native and quotation Currency and available evidence",
          );
      }
      if (selection.priceAvailability === "AVAILABLE" && selection.unitPrice) {
        const same = selection.unitPrice.currency.code === offer.currency.code;
        if (same !== (selection.conversionAvailability === "NOT_REQUIRED"))
          invalid(location, "Only same-Currency native prices require no conversion");
        if (!same && selection.conversionAvailability === "UNAVAILABLE") {
          if (block.costAvailability === "AVAILABLE")
            invalid(path, "Unavailable conversion cannot disclose converted block cost");
          if (
            ["EUR", "USD", "GBP", "NOK"].includes(selection.unitPrice.currency.code) &&
            ["EUR", "USD", "GBP", "NOK"].includes(offer.currency.code) &&
            rates.status !== "UNAVAILABLE"
          )
            invalid(
              location,
              "Unavailable supported conversion requires unavailable publication evidence",
            );
        }
      }
      if (issued && selection.conversionAvailability === "UNAVAILABLE")
        invalid(location, "Issued conversion cannot be unavailable");

      if (issued && selection.priceAvailability === "UNAVAILABLE")
        invalid(location, "Issued source price cannot be unavailable");
    });
    if (
      block.selectedSupplierOffers.some(
        (selection) => selection.priceAvailability === "RESTRICTED",
      ) &&
      !["RESTRICTED", "UNAVAILABLE"].includes(block.costAvailability)
    )
      invalid([...path, "costAvailability"], "Restricted input must protect block cost");
  });
  const pricingFields = [
    ["costAvailability", "chapterSum"],
    ["coefficientAvailability", "coefficient"],
    ["commissionFractionAvailability", "commissionFraction"],
    ["costWithBenefitAvailability", "costWithBenefit"],
    ["commissionAvailability", "commissionAmount"],
    ["totalAvailability", "total"],
    ["salePriceAvailability", "salePrice"],
    ["benefitAvailability", "benefitValue"],
  ] as const;
  evaluation.chapters.forEach((chapter, index) => {
    const path = ["evaluation", "chapters", index];
    const original = evaluationRaw?.chapters[index];
    closed(original, chapter, path, [
      "chapterId",
      ...pricingFields.map(([state]) => state),
      "pricing",
    ]);
    if (chapter.pricing)
      closed(
        original?.pricing,
        chapter.pricing,
        [...path, "pricing"],
        ChapterPricing.keyof().options,
      );
    for (const [state, key] of pricingFields)
      availability(chapter.pricing ?? {}, chapter[state], key, [...path, "pricing"]);
    for (const key of [
      "chapterSum",
      "costWithBenefit",
      "commissionAmount",
      "total",
      "salePrice",
      "benefitValue",
    ] as const) {
      const money = chapter.pricing?.[key];
      if (money && money.currency.code !== offer.currency.code)
        invalid([...path, "pricing", key], "Converted financial Money must use quotation Currency");
    }
    if (!offer.workflow.canReadCoefficients && chapter.coefficientAvailability === "AVAILABLE")
      invalid(
        [...path, "coefficientAvailability"],
        "Coefficient cannot be disclosed without observation authority",
      );
    if (
      issued &&
      pricingFields.some(
        ([state]) => chapter[state] === "UNAVAILABLE" || chapter[state] === "NOT_PRICED",
      )
    )
      invalid(path, "Issued Chapter facts cannot be unavailable or unpriced");
    if (
      evaluation.catalogBlocks.some(
        (block) =>
          block.chapterId.toLowerCase() === chapter.chapterId.toLowerCase() &&
          block.costAvailability === "RESTRICTED",
      ) &&
      chapter.costAvailability === "AVAILABLE"
    )
      invalid(
        [...path, "costAvailability"],
        "Restricted catalog inputs cannot disclose Chapter cost",
      );
  });
  const financials = evaluation.financials;
  const path = ["evaluation", "financials"];
  closed(evaluationRaw?.financials, financials, path, [
    "totalValueAvailability",
    "supplierCostAvailability",
    "commissionAvailability",
    "benefitAvailability",
    "totalValue",
    "supplierCost",
    "commissionAmount",
    "benefitValue",
    "benefitPercentage",
  ] satisfies readonly (keyof typeof financials)[]);
  for (const key of ["totalValue", "supplierCost", "commissionAmount", "benefitValue"] as const) {
    const money = financials[key];
    if (money && money.currency.code !== offer.currency.code)
      invalid([...path, key], "Financial Money must use quotation Currency");
  }
  availability(financials, financials.totalValueAvailability, "totalValue", path);
  availability(financials, financials.supplierCostAvailability, "supplierCost", path);
  availability(financials, financials.commissionAvailability, "commissionAmount", path);
  availability(financials, financials.benefitAvailability, "benefitValue", path);
  availability(financials, financials.benefitAvailability, "benefitPercentage", path);
  if (
    financials.totalValueAvailability === "AVAILABLE" &&
    financials.benefitAvailability === "AVAILABLE" &&
    (financials.totalValue?.amount === 0
      ? financials.benefitPercentage !== null
      : typeof financials.benefitPercentage !== "number")
  )
    invalid(
      [...path, "benefitPercentage"],
      "Null benefit percentage denotes an available zero selling total only",
    );
  if (
    issued &&
    [
      financials.totalValueAvailability,
      financials.supplierCostAvailability,
      financials.commissionAvailability,
      financials.benefitAvailability,
    ].some((state) => state === "UNAVAILABLE" || state === "NOT_PRICED")
  )
    invalid(path, "Issued aggregates cannot be unavailable or unpriced");
}

export function assertCustomerOfferPreparedDocument(
  prepared: CustomerOfferPreparedDocument,
  raw: z.input<typeof PrepareCustomerOfferSendResponse>,
): void {
  closed(raw, prepared, [], PrepareCustomerOfferSendResponse.keyof().options);
  closed(raw.content, prepared.content, ["content"], OfferContent.keyof().options);
  unique(
    prepared.content.chapters.map((chapter) => chapter.chapterId.toLowerCase()),
    ["content", "chapters"],
    "chapterId",
  );
  const scopes: {
    text: CustomerOfferPreparedDocument["content"]["header"];
    raw: z.input<typeof OfferRichTextContent>;
    path: Location;
    scope: "HEADER" | "FOOTER" | "BLOCK";
    chapterId?: string;
    blockId?: string;
  }[] = [
    {
      scope: "HEADER",
      text: prepared.content.header,
      raw: raw.content.header,
      path: ["content", "header"] as Location,
    },
    {
      scope: "FOOTER",
      text: prepared.content.footer,
      raw: raw.content.footer,
      path: ["content", "footer"] as Location,
    },
  ];
  prepared.content.chapters.forEach((chapter, index) => {
    const original = raw.content.chapters[index],
      path: Location = ["content", "chapters", index];
    closed(original, chapter, path, OfferChapter.keyof().options);
    if (typeof original?.applyCommission !== "boolean")
      invalid([...path, "applyCommission"], "Explicit commission applicability is required");
    unique(
      chapter.blocks.map((block) => block.blockId.toLowerCase()),
      [...path, "blocks"],
      "blockId",
    );
    chapter.blocks.forEach((block, blockIndex) => {
      const source = original?.blocks[blockIndex],
        location = [...path, "blocks", blockIndex];
      closed(
        source,
        block,
        location,
        block.blockType === "RICH_TEXT"
          ? OfferRichTextBlock.keyof().options
          : OfferCatalogBlock.keyof().options,
      );
      if (block.blockType === "RICH_TEXT")
        scopes.push({
          scope: "BLOCK",
          chapterId: chapter.chapterId,
          blockId: block.blockId,
          text: block.content,
          raw: source?.blockType === "RICH_TEXT" ? source.content : block.content,
          path: [...location, "content"],
        });
      else
        block.selectedSupplierOffers.forEach((selection, selectionIndex) =>
          closed(
            source?.blockType === "CATALOG_ITEM"
              ? source.selectedSupplierOffers[selectionIndex]
              : undefined,
            selection,
            [...location, "selectedSupplierOffers", selectionIndex],
            OfferSupplierSelection.keyof().options,
          ),
        );
    });
  });
  for (const scope of scopes) {
    closed(scope.raw, scope.text, scope.path, OfferRichTextContent.keyof().options);
    unique(
      scope.text.variables.map((variable) => variable.name),
      [...scope.path, "variables"],
      "name",
    );
    scope.text.variables.forEach((variable, index) => {
      const original = scope.raw.variables[index],
        path = [...scope.path, "variables", index];
      closed(original, variable, path, OfferVariable.keyof().options);
      if (
        variable.value === null ||
        !prepared.variables.some(
          (value) =>
            value.scope === scope.scope &&
            value.name === variable.name &&
            (scope.scope !== "BLOCK" ||
              (value.chapterId?.toLowerCase() === scope.chapterId?.toLowerCase() &&
                value.blockId?.toLowerCase() === scope.blockId?.toLowerCase())),
        )
      )
        invalid(path, "Prepared variables require actual resolved values in the same scope");
      if (variable.value !== null)
        closed(
          original?.value,
          variable.value,
          [...path, "value"],
          variable.value.type === "LITERAL"
            ? OfferLiteralValue.keyof().options
            : OfferBindingValue.keyof().options,
        );
    });
  }
  prepared.catalogItems.forEach((item, index) =>
    closed(
      raw.catalogItems[index],
      item,
      ["catalogItems", index],
      CatalogItemFacts.keyof().options,
    ),
  );
  prepared.chapters.forEach((chapter, index) =>
    closed(raw.chapters[index], chapter, ["chapters", index], ["chapterId", "salePrice"]),
  );
  unique(
    prepared.chapters.map((chapter) => chapter.chapterId.toLowerCase()),
    ["chapters"],
    "chapterId",
  );
  if (
    JSON.stringify(
      prepared.content.chapters.map((chapter) => chapter.chapterId.toLowerCase()).sort(),
    ) !== JSON.stringify(prepared.chapters.map((chapter) => chapter.chapterId.toLowerCase()).sort())
  )
    invalid(["chapters"], "Prepared quotes must cover the actual authored Chapters");
  prepared.variables.forEach((variable, index) =>
    closed(
      raw.variables[index],
      variable,
      ["variables", index],
      ["scope", "chapterId", "blockId", "name", "value"],
    ),
  );
}
